export const ACTIVE_OPERATION_STATUSES = new Set([
  'queued', 'running', 'dispatching', 'provider_accepted', 'indeterminate',
  'reconciling', 'stalled_indeterminate',
]);

export const statusLabel = (status = '') => ({
  awaiting_approval: 'Awaiting approval',
  provider_accepted: 'Accepted by provider',
  stalled_indeterminate: 'Needs manual review',
  indeterminate: 'Confirming provider outcome',
  reconciling: 'Confirming provider state',
  succeeded: 'Completed',
}[status] || status.replaceAll('_', ' ').replace(/^./, (value) => value.toUpperCase()));

export const statusTone = (status) => {
  if (status === 'succeeded') return 'emerald';
  if (['failed', 'rejected', 'cancelled', 'expired', 'stalled_indeterminate'].includes(status)) return 'red';
  if (['awaiting_approval', 'indeterminate', 'reconciling', 'provider_accepted'].includes(status)) return 'amber';
  return 'zinc';
};

export const recoveryGuidance = (status) => {
  if (status === 'indeterminate') {
    return 'The provider may have received this command. Do not submit it again while Cloud Watcher confirms the real state.';
  }
  if (status === 'stalled_indeterminate') {
    return 'Cloud Watcher could not confirm the provider state after repeated checks. An administrator should inspect the provider before retrying.';
  }
  if (status === 'provider_accepted') {
    return 'The provider accepted the command. This is not complete until Cloud Watcher confirms the resulting state.';
  }
  return null;
};

export const capabilityReason = (reason) => ({
  operator_disabled: 'Power requests are disabled by the Cloud Watcher operator.',
  organization_disabled: 'Governed power requests are not enabled for this organization.',
  adapter_disabled: 'Governed commands are not enabled for this provider adapter.',
  adapter_unsupported: 'This provider does not support governed power control for this resource type.',
  worker_unavailable: 'The governed operation worker is unavailable. Try again after an administrator restores it.',
  insufficient_permission: 'Your project role or resource grant does not permit power requests.',
  externally_managed: 'Provider power controls are unavailable for this externally managed server.',
  provider_unavailable: 'Cloud Watcher cannot identify an available provider target for this server.',
  fresh_known_state_required: 'Refresh this server to a known current state before requesting a power change.',
  operation_pending: 'Another governed operation is already pending for this server. Follow it in My Work.',
  already_running: 'This server is already running.',
  already_stopped: 'This server is already stopped.',
}[reason] || 'This power request is not currently available.');

export const capabilityState = (capability = {}) => {
  if (capability.state) return capability.state;
  const reason = capability.reason_code || capability.reason || '';
  if (reason.endsWith('_disabled')) return 'disabled';
  if (['terminal_profile_required', 'terminal_target_unavailable'].includes(reason)) return 'misconfigured';
  if (
    reason.includes('unavailable') || reason.includes('unsupported')
    || ['operation_pending', 'already_running', 'already_stopped', 'externally_managed', 'fresh_known_state_required'].includes(reason)
  ) return 'unavailable';
  return capability.allowed ? 'ready' : 'unauthorized';
};

export const capabilityStateLabel = (capability) => ({
  ready: 'Ready',
  disabled: 'Disabled by policy',
  misconfigured: 'Setup required',
  unauthorized: 'Not authorized',
  unavailable: 'Temporarily unavailable',
}[capabilityState(capability)] || 'Unavailable');

export const capabilityForObservedState = (capabilities, status) => {
  if (status === 'running') return capabilities?.stop_request || capabilities?.stop;
  if (status === 'stopped') return capabilities?.start_request || capabilities?.start;
  return capabilities?.stop_request || capabilities?.start_request || capabilities?.stop || capabilities?.start;
};
